import React, { useState } from "react";
import MUIDataTable from "mui-datatables";
import { 
    Box, IconButton, Tooltip, Typography, Dialog, DialogTitle, 
    DialogContent, DialogActions, Button, Divider, Avatar 
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faQrcode, faCopy, faDownload, faIdBadge } from "@fortawesome/free-solid-svg-icons";
import QRCode from "react-qr-code";

// Ajusta esta ruta a donde tengas tu archivo de variables globales
import { domain } from "../../../apiRoutes/apiBase"; 

export default function EmployeesTable({ employees, onEdit, onDelete }) {
    
    // Estados para el Modal del Código QR
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    // Abrir Modal
    const handleOpenQR = (employee) => {
        setSelectedEmployee(employee);
        setQrModalOpen(true);
    };

    // Cerrar Modal
    const handleCloseQR = () => {
        setQrModalOpen(false);
        setSelectedEmployee(null);
    };

    // Copiar Enlace al Portapapeles
    const handleCopyLink = () => {
        if (!selectedEmployee) return;
        const url = `${domain}businesscard/${selectedEmployee.uuid}`;
        navigator.clipboard.writeText(url);
        alert("¡Enlace de la tarjeta copiado al portapapeles!");
    };

    // Descargar QR como Imagen (PNG)
    const handleDownloadQR = () => {
        if (!selectedEmployee) return;
        
        // Obtenemos el SVG que genera react-qr-code
        const svg = document.getElementById("EmployeeQRCode");
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();
        
        img.onload = () => {
            // Le damos fondo blanco para que no sea transparente al enviarlo por WhatsApp
            canvas.width = img.width + 20; // 20px de padding
            canvas.height = img.height + 20;
            ctx.fillStyle = "white"; 
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            // Dibujamos el QR centrado
            ctx.drawImage(img, 10, 10); 
            
            // Descargamos
            const pngFile = canvas.toDataURL("image/png");
            const downloadLink = document.createElement("a");
            downloadLink.download = `QR_${selectedEmployee.person?.name}_${selectedEmployee.person?.last_name}.png`;
            downloadLink.href = pngFile;
            downloadLink.click();
        };
        
        // Convertimos el SVG a base64 para que la imagen lo pueda leer
        img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    };

    // Configuración de las columnas de MUIDataTable
    const columns = [
        {
            name: "person",
            label: "Asociado",
            options: {
                customBodyRender: (value) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.50', color: 'primary.main', width: 35, height: 35, fontSize: '1rem', fontWeight: 'bold' }}>
                            {value?.name ? value.name.charAt(0).toUpperCase() : 'A'}
                        </Avatar>
                        <Box>
                            <Typography variant="body2" fontWeight="bold">
                                {value?.name} {value?.last_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {value?.email}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        },
        {
            name: "position",
            label: "Cargo / Rol",
            options: {
                customBodyRender: (value) => (
                    <Typography variant="body2" fontWeight="500" color="text.secondary">
                        {value || 'Sin asignar'}
                    </Typography>
                )
            }
        },
        {
            name: "status",
            label: "Estado",
            options: {
                customBodyRender: (value) => (
                    <Box sx={{ 
                        bgcolor: value === 1 ? '#e8f5e9' : '#ffebee', 
                        color: value === 1 ? '#2e7d32' : '#c62828', 
                        px: 1.5, py: 0.5, borderRadius: 2, display: 'inline-block', fontSize: '0.75rem', fontWeight: 'bold'
                    }}>
                        {value === 1 ? 'Activo' : 'Inactivo'}
                    </Box>
                )
            }
        },
        {
            name: "id", // Usamos el ID para indexar la fila completa si es necesario
            label: "Acciones",
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex) => {
                    const emp = employees[dataIndex];
                    return (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Tarjeta de Presentación (QR)">
                                <IconButton size="small" sx={{ color: '#1976d2', bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }} onClick={() => handleOpenQR(emp)}>
                                    <FontAwesomeIcon icon={faQrcode} />
                                </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Editar Asociado">
                                <IconButton size="small" sx={{ color: '#ed6c02', bgcolor: '#fff3e0', '&:hover': { bgcolor: '#ffe0b2' } }} onClick={() => onEdit(emp)}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Desvincular">
                                <IconButton size="small" sx={{ color: '#d32f2f', bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }} onClick={() => onDelete(emp.id)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                }
            }
        }
    ];

    const options = {
        filterType: 'dropdown',
        responsive: 'standard',
        selectableRows: 'none', // Ocultar checkboxes si no los usas
        elevation: 1, // Quitamos la sombra para que se vea limpio
        textLabels: {
            body: { noMatch: "No se encontraron empleados registrados", toolTip: "Ordenar" },
            pagination: { next: "Siguiente", previous: "Anterior", rowsPerPage: "Filas por página:", displayRows: "de" },
            toolbar: { search: "Buscar", downloadCsv: "Descargar CSV", print: "Imprimir", viewColumns: "Ver Columnas", filterTable: "Filtrar Tabla" }
        }
    };

    return (
        <>
            <MUIDataTable
                title={<Typography variant="h6" fontWeight="bold" sx={{ color: '#333' }}>Lista de Asociados</Typography>}
                data={employees}
                columns={columns}
                options={options}
            />

            {/* === MODAL DE LA TARJETA DE PRESENTACIÓN (QR) === */}
            <Dialog open={qrModalOpen} onClose={handleCloseQR} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Avatar sx={{ width: 50, height: 50, bgcolor: 'primary.50', color: 'primary.main', mx: 'auto', mb: 1 }}>
                        <FontAwesomeIcon icon={faIdBadge} size="lg" />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">Tarjeta de Presentación</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {selectedEmployee?.person?.name} {selectedEmployee?.person?.last_name}
                    </Typography>
                </DialogTitle>
                
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2, pb: 3 }}>
                    <Divider sx={{ width: '100%', mb: 3 }} />
                    
                    {/* Contenedor del QR */}
                    <Box sx={{ p: 2, bgcolor: '#fff', border: '1px solid #e0e0e0', borderRadius: 3, display: 'inline-block' }}>
                        <QRCode 
                            id="EmployeeQRCode" // ID clave para poder descargarlo
                            value={`${domain}businesscard/${selectedEmployee?.uuid}`} 
                            size={180} 
                            level="H" // High error correction, ideal si le quieren poner un logo en medio después
                        />
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, textAlign: 'center', maxWidth: 250 }}>
                        Escanea este código para ver el perfil público y agendar con este asociado.
                    </Typography>
                </DialogContent>
                
                <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'center', gap: 2 }}>
                    <Button 
                        variant="outlined" 
                        color="inherit" 
                        startIcon={<FontAwesomeIcon icon={faCopy} />} 
                        onClick={handleCopyLink}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        Copiar Link
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        startIcon={<FontAwesomeIcon icon={faDownload} />} 
                        onClick={handleDownloadQR}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                        disableElevation
                    >
                        Descargar QR
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}